import { type ChangeEvent, useCallback, useState } from 'react';

import { formatDate } from '@/utils/date';
import type { Locale } from '@/utils/localeFormatters';

import { type DayBounds, isDayBlocked, parseTypedDay } from './pickerDays';

export interface TypedDay {
    /** What the input shows: the text being typed, otherwise the picked day. */
    text: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    /** Drops the typed text, so the input shows the day it is given again. */
    reset: () => void;
}

/**
 * The text of a date input that also takes days from elsewhere (a calendar, the page).
 *
 * Every keystroke reports a day: the typed one once it parses and is allowed, otherwise
 * null, so a half-typed or blocked day never stands as the old one. The typed text stays
 * on screen until a new day arrives, so a mistyped "31-09" is still there to correct; a
 * day cleared from outside empties the input unless the user is mid-typing.
 */
export function useTypedDay(
    date: Date | null,
    locale: Locale,
    bounds: DayBounds,
    onDateChange: (date: Date | null) => void,
    onTypedDay: () => void,
    onTextChange?: (text: string, day: Date | null) => void
): TypedDay {
    const [draft, setDraft] = useState<string | null>(null);
    const [shownDate, setShownDate] = useState<Date | null>(date);

    // A new day from outside replaces what was typed, as the input showed it before.
    if ((date?.getTime() ?? null) !== (shownDate?.getTime() ?? null)) {
        setShownDate(date);
        if (date !== null) setDraft(null);
    }

    const onChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const text = event.target.value;
            setDraft(text);
            const parsed = parseTypedDay(text, locale);
            const day = parsed && !isDayBlocked(parsed, bounds) ? parsed : null;
            onTextChange?.(text, day);
            if (day) {
                onDateChange(day);
                onTypedDay();
            } else {
                onDateChange(null);
            }
        },
        [locale, bounds, onDateChange, onTypedDay, onTextChange]
    );

    const reset = useCallback(() => setDraft(null), []);

    // An emptied input shows the day it holds again; only text that is not a day stays.
    return { text: draft || (date ? formatDate(date, 'display') : ''), onChange, reset };
}
